/** @jsxRuntime classic */
/** @jsx jsx */

import React from 'react';
import { jsx } from '@emotion/core';

import { Card } from 'src/components/Card';
import { Icon } from 'src/components/Icon';
import { Colors, Margins, Paddings, Responsive } from 'src/design-system/style-types';

import { style } from './CardSelectable.style';

export type Variant = 'light' | 'accent';

interface Props {
  children: React.ReactNode;
  title?: string;
  tabIndex?: number;
  className?: string;
  padding?: Paddings | undefined;
  fullWidth?: boolean;
  fullHeight?: boolean;
  selected?: boolean;
  onClick?: (selected: boolean) => void;
  variant?: Variant;
  noHighlight?: boolean;
  noShadow?: boolean;
  borderColor?: Colors;
  marginBottom?: Responsive<Margins>;
  marginLeft?: Responsive<Margins>;
  showCheck?: boolean;
  disabled?: boolean;
  hoverable?: boolean;
}

export const CardSelectable = ({
  children,
  title,
  tabIndex,
  className = '',
  padding = undefined,
  fullWidth = true,
  fullHeight = false,
  selected = false,
  onClick = () => {},
  variant = 'light',
  borderColor = 'transparent',
  noHighlight = false,
  noShadow = false,
  marginBottom = '0',
  marginLeft = '0',
  showCheck = true,
  disabled = false,
  hoverable = true,
}: Props) => {
  const handleClick = () => {
    if (!disabled) {
      onClick(!selected);
    }
  };

  const getPadding = (): Paddings => {
    let paddingValue;

    if (padding) {
      paddingValue = padding;
    } else if (variant === 'light') {
      paddingValue = 'lg';
    } else if (variant === 'accent') {
      paddingValue = 'xl';
    }

    return paddingValue as Paddings;
  };

  return (
    <button
      type="button"
      tabIndex={tabIndex}
      data-qa="card-selectable"
      onClick={handleClick}
      css={style.container({ fullHeight, fullWidth, disabled })}
    >
      <Card
        data-qa="card"
        padding={getPadding()}
        fullWidth={fullWidth}
        fullHeight={fullHeight}
        css={style.card({ selected, variant, borderColor, noHighlight, noShadow, hoverable, disabled })}
        className={className}
        marginBottom={marginBottom}
        marginLeft={marginLeft}
        title={title}
      >
        {selected && showCheck && <Icon css={style.icon({ variant })} name="check circle alt" />}
        {children}
      </Card>
    </button>
  );
};
